import { useEffect, useRef, useState } from 'react';
import { FtIn, type BarClass, type FunctionCode, type Line, type MaterialType } from '@otto/rebar-engine';
import { COLORS, formInput, formSelect, ghostBtn, mono, primaryBtn } from './styles';
import { formToLine, type LineFormState } from './helpers';

interface Props {
  segmentId: string;
  initial: LineFormState;
  onSave: (line: Line, advance: boolean) => void;
  onCancel: () => void;
}

const MATERIALS: MaterialType[] = ['TR', 'GI', 'MT', 'TP', 'TI'];
const FUNCTIONS: FunctionCode[] = ['BF', 'TC', 'TA', 'TB', 'TD', 'SPX', 'RA', 'Sheets'];
const CLASSES: BarClass[] = ['S', 'L', 'H'];

type FtInKey = 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'span' | 'spacing' | 'stockLength';
const FT_IN_FIELDS: FtInKey[] = ['L1', 'L2', 'L3', 'L4', 'L5', 'span', 'spacing', 'stockLength'];

export function LineForm({ segmentId, initial, onSave, onCancel }: Props) {
  const [form, setForm] = useState<LineFormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<FtInKey, string>>>({});
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  const set = (k: keyof LineFormState, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    firstInputRef.current?.focus();
    firstInputRef.current?.select();
  }, []);

  function validateFtIn(k: FtInKey, val: string) {
    if (!val.trim()) {
      setErrors((e) => {
        const n = { ...e };
        delete n[k];
        return n;
      });
      return;
    }
    try {
      FtIn.parse(val.trim());
      setErrors((e) => {
        const n = { ...e };
        delete n[k];
        return n;
      });
    } catch {
      setErrors((e) => ({ ...e, [k]: 'Invalid' }));
    }
  }

  function doSave(advance: boolean) {
    // Validate all FtIn fields up front
    const fresh: typeof errors = {};
    for (const k of FT_IN_FIELDS) {
      const v = form[k];
      if (v.trim()) {
        try {
          FtIn.parse(v.trim());
        } catch {
          fresh[k] = 'Invalid';
        }
      }
    }
    if (Object.keys(fresh).length > 0) {
      setErrors(fresh);
      return;
    }

    const line = formToLine(form, segmentId);
    if (!line) {
      alert('Invalid line number');
      return;
    }
    onSave(line, advance);
  }

  function onFormKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        doSave(true);
      }}
      onKeyDown={onFormKeyDown}
      style={{
        margin: '6px 0 6px 28px',
        padding: 12,
        background: 'rgba(255,255,255,0.02)',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={gridRow(3)}>
        <Field label="LINE #">
          <input
            ref={firstInputRef}
            value={form.lineNumber}
            onChange={(e) => set('lineNumber', e.target.value)}
            style={formInput}
          />
        </Field>
        <Field label="MATERIAL">
          <select
            value={form.material}
            onChange={(e) => set('material', e.target.value as MaterialType)}
            style={formSelect}
          >
            {MATERIALS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
        <Field label="FUNCTION">
          <select
            value={form.function}
            onChange={(e) => set('function', e.target.value as FunctionCode)}
            style={formSelect}
          >
            {FUNCTIONS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div style={gridRow(4)}>
        <Field label="SIZE">
          <input
            value={form.size}
            placeholder="#5"
            onChange={(e) => set('size', e.target.value)}
            style={formInput}
          />
        </Field>
        <Field label="CLASS">
          <select
            value={form.class}
            onChange={(e) => set('class', e.target.value as BarClass)}
            style={formSelect}
          >
            {CLASSES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="QTY">
          <input
            value={form.quantity}
            onChange={(e) => set('quantity', e.target.value)}
            style={formInput}
          />
        </Field>
        <Field label="MULT">
          <input
            value={form.multiplier}
            onChange={(e) => set('multiplier', e.target.value)}
            style={formInput}
          />
        </Field>
      </div>

      <div style={gridRow(5)}>
        {(['L1', 'L2', 'L3', 'L4', 'L5'] as const).map((k) => (
          <Field key={k} label={k} error={errors[k]}>
            <input
              value={form[k]}
              placeholder="6'-0&quot;"
              onChange={(e) => set(k, e.target.value)}
              onBlur={(e) => validateFtIn(k, e.target.value)}
              style={{
                ...formInput,
                borderColor: errors[k] ? COLORS.red : 'rgba(255,255,255,0.12)',
              }}
            />
          </Field>
        ))}
      </div>

      <div style={gridRow(4)}>
        <Field label="LAP">
          <input
            value={form.lap}
            placeholder="40D"
            onChange={(e) => set('lap', e.target.value)}
            style={formInput}
          />
        </Field>
        <Field label="STOCK LEN" error={errors.stockLength}>
          <input
            value={form.stockLength}
            placeholder="40'-0&quot;"
            onChange={(e) => set('stockLength', e.target.value)}
            onBlur={(e) => validateFtIn('stockLength', e.target.value)}
            style={{
              ...formInput,
              borderColor: errors.stockLength ? COLORS.red : 'rgba(255,255,255,0.12)',
            }}
          />
        </Field>
        <Field label="SPAN" error={errors.span}>
          <input
            value={form.span}
            onChange={(e) => set('span', e.target.value)}
            onBlur={(e) => validateFtIn('span', e.target.value)}
            style={{
              ...formInput,
              borderColor: errors.span ? COLORS.red : 'rgba(255,255,255,0.12)',
            }}
          />
        </Field>
        <Field label="SPACING" error={errors.spacing}>
          <input
            value={form.spacing}
            onChange={(e) => set('spacing', e.target.value)}
            onBlur={(e) => validateFtIn('spacing', e.target.value)}
            style={{
              ...formInput,
              borderColor: errors.spacing ? COLORS.red : 'rgba(255,255,255,0.12)',
            }}
          />
        </Field>
      </div>

      <div style={gridRow(3)}>
        <Field label="PLACING">
          <input
            value={form.placing}
            onChange={(e) => set('placing', e.target.value)}
            style={formInput}
          />
        </Field>
        <Field label="GRADE">
          <input
            value={form.grade}
            placeholder="60"
            onChange={(e) => set('grade', e.target.value)}
            style={formInput}
          />
        </Field>
        <Field label="NOTES">
          <input
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            style={formInput}
          />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
        <button type="button" onClick={() => doSave(false)} style={primaryBtn}>
          Save
        </button>
        <button type="button" onClick={() => doSave(true)} style={{ ...primaryBtn, background: 'transparent' }}>
          Save & next
        </button>
        <button type="button" onClick={onCancel} style={ghostBtn}>
          Cancel
        </button>
        <span style={{ ...mono(10, COLORS.textDim), marginLeft: 'auto' }}>
          Enter saves &amp; advances · Esc cancels
        </span>
      </div>
    </form>
  );
}

function gridRow(cols: number): React.CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gap: 8,
  };
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={mono(9, error ? COLORS.red : COLORS.textDim)}>
        {label}
        {error ? ` · ${error}` : ''}
      </span>
      {children}
    </label>
  );
}
